// PC enrollment API — anonymous browser flow on the PC + phone-side
// approve. See STATUS.md §"Onboarding redesign" and the threat model.
// All four endpoints have specific contracts; security boundaries (OOB,
// IP binding, one-time claim) are enforced server-side.
import axios from "axios";

import { api } from "./client";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8001";

// Anonymous axios — never sends the dashboard's Bearer token. The /join
// page must not leak the owner's auth even if they happen to be logged
// in on the same browser somehow.
const anon = axios.create({
  baseURL: `${baseURL}/api/v1`,
  timeout: 15000,
});

export type PCEnrollmentStatus =
  | "requested"
  | "approved"
  | "claimed"
  | "denied"
  | "expired";

export type CreateSessionResponse = {
  code: string;
  oob_code: string;
  expires_at: string;
  approve_url: string;
};

export type SessionStatusResponse = {
  status: PCEnrollmentStatus;
  expires_at: string;
};

export type OwnerViewResponse = {
  code: string;
  status: PCEnrollmentStatus;
  hostname_hint: string;
  origin_ip: string;
  user_agent: string;
  created_at: string;
  expires_at: string;
  is_terminal: boolean;
  failed_oob_attempts: number;
  max_oob_attempts: number;
};

// Used by the /join page — anonymous, no auth header.
export async function createPCEnrollmentSession(
  hostnameHint?: string,
): Promise<CreateSessionResponse> {
  const { data } = await anon.post<CreateSessionResponse>(
    "/agents/pc-enrollment/sessions",
    { hostname_hint: hostnameHint ?? "" },
  );
  return data;
}

// Used by the /join page polling — anonymous.
export async function getPCEnrollmentStatus(
  code: string,
): Promise<SessionStatusResponse> {
  const { data } = await anon.get<SessionStatusResponse>(
    `/agents/pc-enrollment/sessions/${encodeURIComponent(code)}`,
  );
  return data;
}

// Used by the /approve page — needs auth (the dashboard's Bearer).
export async function getPCEnrollmentOwnerView(
  code: string,
): Promise<OwnerViewResponse> {
  const { data } = await api.get<OwnerViewResponse>(
    `/agents/pc-enrollment/sessions/${encodeURIComponent(code)}/owner-view`,
  );
  return data;
}

// Used by the /join page — anonymous. Asks the backend to email the
// owner identified by `officeCode` with a link to the approve page.
//
// Response is intentionally generic (no leak of whether the office_code
// resolved) — anti-enumeration. UI shows the same "request sent"
// message either way.
export async function notifyPCEnrollmentOwner(
  code: string,
  officeCode: string,
): Promise<{ sent: boolean }> {
  const { data } = await anon.post<{ sent: boolean }>(
    `/agents/pc-enrollment/sessions/${encodeURIComponent(code)}/notify`,
    { office_code: officeCode },
  );
  return data;
}

// Used by the /approve page — owner role only, MFA gate, OOB code required.
export async function approvePCEnrollmentSession(
  code: string,
  oobCode: string,
): Promise<{ status: PCEnrollmentStatus; approved_at: string }> {
  const { data } = await api.post<{
    status: PCEnrollmentStatus;
    approved_at: string;
  }>(`/agents/pc-enrollment/sessions/${encodeURIComponent(code)}/approve`, {
    oob_code: oobCode,
  });
  return data;
}

// Returns the URL to point a <a download> at. Browser handles the
// fetch + filename via the backend's Content-Disposition header.
export function pcEnrollmentInstallerUrl(code: string): string {
  // Filename intentionally includes the code so the agent can parse it
  // from os.Executable() at launch.
  const filename = `kraetdnet-agent-${code}.exe`;
  return `${baseURL}/api/v1/agents/pc-enrollment/installer/${encodeURIComponent(
    code,
  )}/${filename}`;
}
